// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { JobRequisitionStatus } from "@config/constant";
import { Role } from "./types";
import { tokens } from "../../src/theme";
import { CareerFunction, Company } from "@slices/employeeSlice/model";

export const isIncludedRole = (a: Role[], b: Role[]): boolean => {
  return [...getCrossItems(a, b), ...getCrossItems(b, a)].length > 0;
};

function getCrossItems<Role>(a: Role[], b: Role[]): Role[] {
  return a.filter((element) => {
    return b.includes(element);
  });
}

export const toTitleCase = (input: string): string => {
  return input
    .toLocaleLowerCase()
    .split(" ")
    .map((word: string): string => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

export const extractDate = (dateAndTime: string): string => {
  const dateObject = new Date(dateAndTime);

  // Extract the date part
  const dateOnly = dateObject.toISOString().split("T")[0];
  return dateOnly;
};

//Find location ID by office ID
export const findLocationId = (
  locations: Company[],
  companyId: number,
): string | null => {
  const location = locations.find((loc) => loc.id === companyId);

  return location ? location.id.toString() : null;
};

//Find career function by designation ID
export const findCareerFunction = (
  careerFunctions: CareerFunction[],
  designationId: number,
): string => {
  const filteredCareerFunction = careerFunctions.filter((careerFunction) =>
    careerFunction.designations.find(
      (designation) => designation.id === designationId,
    ),
  );
  return filteredCareerFunction[0]?.careerFunction;
};

//Get the button color by status
export const getButtonColor = (status: string): string[] => {
  const color =
    status === JobRequisitionStatus.IN_REVIEW
      ? [
          tokens("dark").customColors.reviewButton,
          tokens("dark").customColors.reviewButtonHover,
        ]
      : status === JobRequisitionStatus.CREATED
        ? [
            tokens("dark").customColors.startReviewButton,
            tokens("dark").customColors.startReviewButtonHover,
          ]
        : [
            tokens("dark").customColors.viewButton,
            tokens("dark").customColors.viewButtonHover,
          ];
  return color;
};

// Get Ordinal suffix based on the number
export const getOrdinalSuffix = (num: number): string => {
  const suffixes = ["th", "st", "nd", "rd"];
  const remainder = num % 100;
  return (
    num +
    (suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0])
  );
};

// Check if the user has the required role
export const hasRequiredRole = (
  roles: string[],
  requiredRoles: string[],
): boolean => {
  return requiredRoles.some((role) => roles.includes(role));
};

export interface CandidateValidationResult {
  isValid: boolean;
  missingFields: string[];
}

export const validateCandidateForHiring = (candidate: {
  firstName?: string | null;
  lastName?: string | null;
  gender?: string | null;
  personalEmail?: string | null;
  contactNo?: string | null;
  address?: string | null;
}): CandidateValidationResult => {
  const missingFields: string[] = [];

  if (!candidate.firstName || candidate.firstName.trim() === "") {
    missingFields.push("First Name");
  }
  if (!candidate.lastName || candidate.lastName.trim() === "") {
    missingFields.push("Last Name");
  }
  if (!candidate.gender) {
    missingFields.push("Gender");
  }
  if (!candidate.personalEmail || candidate.personalEmail.trim() === "") {
    missingFields.push("Personal Email");
  }
  if (!candidate.contactNo || candidate.contactNo.trim() === "") {
    missingFields.push("Contact Number");
  }
  if (!candidate.address || candidate.address.trim() === "") {
    missingFields.push("Address");
  }

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
};

export const formatLocation = (location?: string): string => {
  if (!location) return "";

  return location
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export function applyHighlight(range: Range, commentId: string): void {
  const mark = document.createElement("mark");
  mark.dataset.commentId = commentId;
  mark.style.cssText = `
    background-color: #FFF176;
    border-radius: 2px;
    padding: 1px 0;
  `;

  try {
    range.surroundContents(mark);
  } catch {
    const fragment = range.extractContents();
    mark.appendChild(fragment);
    range.insertNode(mark);
  }
}

export function removeHighlight(commentId: string): void {
  const marks = document.querySelectorAll<HTMLElement>(
    `mark[data-comment-id="${commentId}"]`,
  );
  marks.forEach((mark) => {
    const parent: Node | null = mark.parentNode;
    if (!parent) return;
    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark);
    }
    parent.removeChild(mark);
    (parent as Element).normalize?.();
  });
}
